import type {
  IClientPlan,
  IClientPlanAddonApi,
  IClientPlanApi,
  IPlanBenefit,
  IPlanBenefits
} from '../../interfaces/clients';
import type { ICommercialPlanBundle, IQuote } from '../../interfaces/quotes';
import { TRANSLATIONS_CLIENTS } from '../../translations/clients';
import { ClientPlanStatus } from '../../types/clients';
import { CommercialAddonType } from '../../types/quotes';
import { addDays, addYears, formatDateISO, getFirstOfNextMonth, parseDateISO } from '../dates/dates';
import { round2 } from '../numbers/numbers';
import { addonOptionLabel, type CatalogAddonById } from '../quotes/quotes';

const isAddonInForce = (addon: IClientPlanAddonApi, currencyCode: string, today: string): boolean =>
  addon.currencyCode === currencyCode && (!addon.startDate || addon.startDate <= today);

export const resolvePlanMonthlyRate = (plan: IClientPlanApi, today: string): number | null => {
  if (plan.price == null) return null;

  const addons = (plan.addons ?? []).filter((addon) => isAddonInForce(addon, plan.currencyCode, today));

  return round2(addons.reduce((total, addon) => total + addon.price, plan.price));
};

const bundlesById = (commercialPlans: ICommercialPlanBundle[]): Map<number, ICommercialPlanBundle> =>
  new Map(commercialPlans.map((bundle) => [bundle.id, bundle]));

export const resolveQuoteBundle = (
  quote: IQuote,
  bundles: Map<number, ICommercialPlanBundle>
): ICommercialPlanBundle | null => {
  const planAddon = (quote.commercialAddons ?? []).find((addon) => addon.entity === 'plan');

  return planAddon ? (bundles.get(planAddon.commercialAddonId) ?? null) : null;
};

export const mapApiPlansToClientPlans = (
  plans: IClientPlanApi[],
  quotes: IQuote[] = [],
  commercialPlans: ICommercialPlanBundle[] = []
): IClientPlan[] => {
  const quoteNameById = new Map(quotes.map((quote) => [quote.id, quote.name]));
  const bundles = bundlesById(commercialPlans);
  const today = formatDateISO(new Date());

  return plans.map((plan) => ({
    id: plan.id,
    quoteId: plan.quoteId,
    name: quoteNameById.get(plan.quoteId) ?? plan.name,
    startDate: plan.contractStartDate || null,
    planName: bundles.get(plan.commercialPlanId)?.name ?? plan.name ?? null,
    employeesWithAccess: plan.numberOfLicenses ?? null,
    monthlyRate: resolvePlanMonthlyRate(plan, today),
    currencyCode: plan.currencyCode ?? null,
    addonIds: (plan.addons ?? []).map((addon) => addon.commercialAddonId)
  }));
};

export const mapQuotesToClientPlans = (
  quotes: IQuote[],
  commercialPlans: ICommercialPlanBundle[] = []
): IClientPlan[] => {
  const bundles = bundlesById(commercialPlans);

  return quotes
    .filter((quote) => quote.status === 'active')
    .map((quote) => {
      const bundle = resolveQuoteBundle(quote, bundles);

      return {
        id: quote.id,
        quoteId: quote.id,
        name: quote.name,
        segment: quote.segment,
        startDate: quote.startDate || null,
        planName: bundle?.name ?? null,
        monthlyRate: bundle?.suggestedPrice ?? null,
        currencyCode: bundle?.currencyCode ?? null,
        addonIds: (quote.commercialAddons ?? []).map((addon) => addon.commercialAddonId)
      };
    });
};

export const resolveClientPlanStatus = (
  startDate: string | null,
  enrollmentWindowDays: number,
  today: Date = new Date()
): ClientPlanStatus => {
  if (!startDate) return ClientPlanStatus.AwaitingStart;

  const todayISO = formatDateISO(today);
  const enrollmentEnd = formatDateISO(addDays(parseDateISO(startDate), enrollmentWindowDays));

  if (todayISO < startDate) return ClientPlanStatus.AwaitingStart;
  if (todayISO < enrollmentEnd) return ClientPlanStatus.Enrolling;
  return ClientPlanStatus.Active;
};

export const resolveAppOnlyPlanStatus = (startDate: string | null, today: Date = new Date()): ClientPlanStatus => {
  if (!startDate) return ClientPlanStatus.AwaitingStart;

  return formatDateISO(today) < startDate ? ClientPlanStatus.AwaitingStart : ClientPlanStatus.Active;
};

const MS_PER_DAY = 86_400_000;

export const getEnrollmentDaysLeft = (
  startDate: string | null,
  enrollmentWindowDays: number,
  today: Date = new Date()
): number => {
  if (!startDate) return 0;

  const end = addDays(parseDateISO(startDate), enrollmentWindowDays);
  const todayMidnight = parseDateISO(formatDateISO(today));
  return Math.max(0, Math.round((end.getTime() - todayMidnight.getTime()) / MS_PER_DAY));
};

export const categorizePlanAddons = (addonIds: number[], catalogById: CatalogAddonById): IPlanBenefits => {
  const groups: IPlanBenefits = { planCoverage: [], voluntary: [] };

  for (const addonId of addonIds) {
    const entry = catalogById.get(addonId);

    if (!entry) continue;

    const { addon, type, groupName } = entry;
    const benefit: IPlanBenefit = {
      id: addonId,
      name: groupName || addon.name,
      detail: groupName ? addonOptionLabel(groupName, addon.name) : null
    };

    if (type === CommercialAddonType.Voluntary) groups.voluntary.push(benefit);
    else groups.planCoverage.push(benefit);
  }

  return groups;
};

export interface IClientPlanDates {
  enrollmentEnd: string | null;
  effectiveDate: string | null;
  coverageStart: string | null;
  renewalDate: string | null;
}

export const deriveClientPlanDates = (startDate: string | null, enrollmentWindowDays: number): IClientPlanDates => {
  if (!startDate) {
    return { enrollmentEnd: null, effectiveDate: null, coverageStart: null, renewalDate: null };
  }

  const closes = addDays(parseDateISO(startDate), enrollmentWindowDays);
  const effective = getFirstOfNextMonth(closes);
  const effectiveDate = formatDateISO(effective);

  return {
    enrollmentEnd: formatDateISO(closes),
    effectiveDate,
    coverageStart: effectiveDate,
    renewalDate: formatDateISO(addYears(effective, 1))
  };
};

export const deriveAppOnlyPlanDates = (startDate: string | null): IClientPlanDates => {
  if (!startDate) {
    return { enrollmentEnd: null, effectiveDate: null, coverageStart: null, renewalDate: null };
  }

  return {
    enrollmentEnd: null,
    effectiveDate: startDate,
    coverageStart: startDate,
    renewalDate: formatDateISO(addYears(parseDateISO(startDate), 1))
  };
};

export interface IClientPlanSummaryItem {
  key: string;
  params?: Record<string, string | number>;
}

export const getClientPlanSummary = (
  status: ClientPlanStatus,
  dates: IClientPlanDates,
  startDate: string | null,
  daysLeft: number,
  formatDate: (iso: string | null) => string | null
): IClientPlanSummaryItem[] => {
  const S = TRANSLATIONS_CLIENTS.profile.plans.summary;

  if (status === ClientPlanStatus.Active) {
    const items: IClientPlanSummaryItem[] = [];
    const effective = formatDate(dates.effectiveDate);
    if (effective) items.push({ key: S.activeSince, params: { date: effective } });
    const renewal = formatDate(dates.renewalDate);
    if (renewal) items.push({ key: S.renews, params: { date: renewal } });
    return items;
  }

  if (status === ClientPlanStatus.Enrolling) {
    return [{ key: S.daysLeft, params: { days: daysLeft } }];
  }

  const opens = formatDate(startDate);
  const items: IClientPlanSummaryItem[] = [
    opens ? { key: S.enrollmentOpens, params: { date: opens } } : { key: S.notScheduled }
  ];
  const coverage = formatDate(dates.coverageStart);
  if (coverage) items.push({ key: S.coverageStarts, params: { date: coverage } });
  return items;
};

export const getAppOnlyPlanSummary = (
  status: ClientPlanStatus,
  startDate: string | null,
  renewalDate: string | null,
  formatDate: (iso: string | null) => string | null
): IClientPlanSummaryItem[] => {
  const S = TRANSLATIONS_CLIENTS.profile.plans.summary;

  const start = formatDate(startDate);
  const items: IClientPlanSummaryItem[] = [];

  if (!start) items.push({ key: S.startDateNotSet });
  else if (status === ClientPlanStatus.Active) items.push({ key: S.activeSince, params: { date: start } });
  else items.push({ key: S.starts, params: { date: start } });

  const renewal = formatDate(renewalDate);
  if (renewal) items.push({ key: S.renews, params: { date: renewal } });

  return items;
};
